import React from "react";
import Modal from "../Modal";
import { STORAGE_KEYS } from "../../../lib/constants";

const DEFAULT_STATS = {
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    guessDistribution: [0, 0, 0, 0, 0], // 0-4 mistakes
};

function StatsModal({ isOpen, onClose }) {
    // Get stats from localStorage when the modal opens
    const [stats, setStats] = React.useState(DEFAULT_STATS);

    React.useEffect(() => {
        if (!isOpen) return;
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.gameStats);
            if (stored) {
                setStats(JSON.parse(stored));
                return;
            }
        } catch (e) {
            console.error("Error loading stats:", e);
        }
        setStats(DEFAULT_STATS);
    }, [isOpen]);

    const winPercentage = stats.gamesPlayed > 0
        ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100)
        : 0;

    const maxGuesses = Math.max(...stats.guessDistribution, 1);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="STATISTICS"
        >
            <div className="space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-2">
                    <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                        <span className="text-2xl font-bold text-slate-800">{stats.gamesPlayed}</span>
                        <span className="text-xs text-slate-500 mt-1 text-center">Played</span>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                        <span className="text-2xl font-bold text-slate-800">{winPercentage}</span>
                        <span className="text-xs text-slate-500 mt-1 text-center">Win %</span>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                        <span className="text-2xl font-bold text-slate-800">{stats.currentStreak}</span>
                        <span className="text-xs text-slate-500 mt-1 text-center">Streak</span>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                        <span className="text-2xl font-bold text-slate-800">{stats.maxStreak}</span>
                        <span className="text-xs text-slate-500 mt-1 text-center">Best</span>
                    </div>
                </div>

                {/* Guess distribution */}
                <div className="border-t border-slate-200 pt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Mistakes Distribution
                    </p>
                    <div className="space-y-1.5">
                        {stats.guessDistribution.map((count, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <span className="w-4 text-sm font-medium text-slate-600">
                                    {index}
                                </span>
                                <div className="flex-1 flex items-center">
                                    <div
                                        className="h-6 rounded flex items-center justify-end px-2 transition-all"
                                        style={{
                                            width: `${Math.max((count / maxGuesses) * 100, 8)}%`,
                                            backgroundColor: count > 0 ? 'hsl(var(--foreground))' : 'hsl(var(--muted))',
                                            minWidth: '24px'
                                        }}
                                    >
                                        <span
                                            className="text-xs font-medium"
                                            style={{
                                                color: count > 0 ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))'
                                            }}
                                        >
                                            {count}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* No games message */}
                {stats.gamesPlayed === 0 && (
                    <div className="text-center py-2 text-gray-500">
                        <p className="text-sm">No games played yet!</p>
                        <p className="text-xs mt-1">Complete a puzzle to see your stats.</p>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default StatsModal;
